import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpAuthHelperService } from './http-auth-helper.service';
import { CaseConverter } from '../helpers/case-converter.helper';
import { lastValueFrom } from 'rxjs';
import { FileUploadService } from './file-upload.service';

@Injectable({
  providedIn: 'root',
})
export class AdminArtifactsSearchService {
  private baseUrl = environment.endpoint;

  constructor(
    private httpAuthHelperService: HttpAuthHelperService,
    private fileUploadService: FileUploadService
  ) { }

  async getArtifacts(uuid: string): Promise<any> {
    const data = await this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-lookup/search`,
      { uuid }
    );
    if (data?.body?.response) return data.body.response;
    if (data?.response) return data.response;
    throw new Error('Unexpected response');
  }

  async getFieldMapping(): Promise<any> {
    const data = await this.httpAuthHelperService.requestPromise(
      'GET',
      `${this.baseUrl}/artifacts-lookup/fields-tags-mapping`,
      {}
    );
    if (data?.body?.response) return data.body.response;
    if (data?.response) return data.response;
    throw new Error('Unexpected response');
  }

  async getArtifactsSignedLinks(keys: string[]): Promise<any> {
    const data = await this.httpAuthHelperService
      .requestPromise('POST', `${this.baseUrl}/artifacts-lookup/assets`, {
        keys,
      })
      .catch((err) => {
        console.error('Error fetching artifact signed links:', err);
        throw new Error('Failed to fetch artifact signed links');
      });
    if (data?.body?.response) return data.body.response;
    if (data?.response) return data.response;
    throw new Error('Unexpected response');
  }

  async getUserForms(uuid: string): Promise<any> {
    try {
      const data = await this.httpAuthHelperService.requestPromise(
        'POST',
        `${this.baseUrl}/artifacts-formdata/search-user-forms`,
        { userdata_uuid: uuid }
      );
      if (data?.body?.response) {
        return CaseConverter.keysToCamelCase(data.body.response, ['table']);
      }
      throw new Error('Unexpected response');
    } catch (err) {
      console.error('failed to get user form data', err);
      throw err;
    }
  }
  async saveUserForm(
    userUUID: string,
    uuid: string,
    formUUID: string,
    data: any,
    completedCount: any,
    submitted: boolean = false,
    forcedTermination: boolean = false,
    currentIdentifierValue: string | null = null
  ): Promise<any> {
    return this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-formdata/save-data`,
      {
        uuid,
        userUUID,
        formUUID,
        completedCount,
        submitted,
        forcedTermination,
        data: CaseConverter.keysToSnakeCase(data),
        currentIdentifierValue,
      }
    );
  }
  async reOpenForm(uuid: string, userUUID: string): Promise<any> {
    return this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-formdata/re-open-form`,
      { uuid, userUUID }
    );
  }

  async reOpenUntilForm(
    uuid: string,
    userUUID: string,
    untilDate: number
  ): Promise<any> {
    return this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-formdata/re-open-until-user-form`,
      { uuid, userUUID, untilDate }
    );
  }

  async getSignedUrl(key: string, mimeType: string): Promise<string> {
    const data = await this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-formdata/create-signed-link`,
      { key, mimeType }
    );
    if (data?.body?.response?.signedUrl) {
      return data.body.response.signedUrl;
    }
    throw new Error('Invalid response');
  }

  async uploadAssetViaSignedLink(
    key: string,
    file: File,
    onProgress: (progress: number) => void
  ): Promise<any> {
    try {
      const signedUrl = await this.getSignedUrl(key, file.type);
      if (!signedUrl) {
        throw new Error('Signed URL is undefined or empty.');
      }
      await lastValueFrom(
        this.fileUploadService.uploadFileToS3(signedUrl, file, onProgress)
      );
    } catch (err) {
      console.error('Upload failed:', err);
      throw new Error('Upload initialization failed');
    }
  }

  async checkArtifactExists(key: string): Promise<any> {
    const data = await this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-formdata/check-artifact-export`,
      { key }
    );
    if (data?.body?.response) return data.body.response;
    throw new Error('Invalid response');
  }
  async getArtifactFile(key: string, bucket: string | null): Promise<string> {
    const data = await this.httpAuthHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/artifacts-formdata/get-artifact-signed-link`,
      { key, bucket }
    );
    if (data?.body?.response?.signedUrl) {
      return data.body.response.signedUrl;
    }
    throw new Error('Invalid response');
  }
}
